import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Loader2, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { DISCORD_URL, CHECKOUT_LINKS, API } from "@/lib/config";

const GAMES = [
  "Fortnite", "Valorant", "Counter-Strike 2", "Apex Legends", "Warzone",
  "Rainbow Six Siege", "Rust", "The Finals", "Overwatch 2", "Marvel Rivals",
];

const PLAN_LABELS = {
  monthly: "Monthly License — €30 / month",
  lifetime: "Lifetime License — €165 one-time",
  woofer_once: "Woofer One-Time — €25",
  woofer_lifetime: "Woofer Lifetime — €60 one-time",
};

const OrderDialog = ({ plan, open, onOpenChange }) => {
  const [game, setGame] = useState(GAMES[0]);
  const [discordUser, setDiscordUser] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);

  const reset = () => {
    setOrder(null);
    setDiscordUser("");
    setEmail("");
    setGame(GAMES[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!discordUser.trim()) {
      toast.error("Enter your Discord username to continue.");
      return;
    }
    if (!email.trim()) {
      toast.error("Enter your email for the confirmation receipt.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/orders`, {
        plan,
        game,
        discord_username: discordUser.trim(),
        email: email.trim(),
      });
      setOrder(res.data);
      toast.success("Order created — confirmation email sent.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create order. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyReference = async () => {
    try {
      await navigator.clipboard.writeText(order.reference);
      toast.success("Reference copied.");
    } catch {
      toast.error("Copy failed — select and copy manually.");
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) setTimeout(reset, 200);
      }}
    >
      <DialogContent
        data-testid="order-dialog"
        className="bg-[#101116] border border-red-500/30 text-zinc-100 sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-wide text-white">
            {order ? "Order Locked In" : "Start Your Order"}
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs font-mono-x uppercase tracking-wider">
            {plan ? PLAN_LABELS[plan] : ""}
          </DialogDescription>
        </DialogHeader>

        {!order ? (
          <form onSubmit={handleSubmit} className="space-y-5 mt-2">
            <div>
              <label htmlFor="order-game" className="block font-mono-x text-[10px] uppercase tracking-widest text-[#ff4655] mb-2">
                Your Game
              </label>
              <select
                id="order-game"
                data-testid="order-game-select"
                value={game}
                onChange={(e) => setGame(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-sm px-3 py-2.5 focus:outline-none focus:border-[#ff4655]"
              >
                {GAMES.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="order-discord" className="block font-mono-x text-[10px] uppercase tracking-widest text-[#ff4655] mb-2">
                Discord Username
              </label>
              <input
                id="order-discord"
                data-testid="order-discord-input"
                type="text"
                value={discordUser}
                onChange={(e) => setDiscordUser(e.target.value)}
                placeholder="e.g. player#0001 or @player"
                className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-sm px-3 py-2.5 placeholder:text-zinc-600 focus:outline-none focus:border-[#ff4655]"
              />
            </div>
            <div>
              <label htmlFor="order-email" className="block font-mono-x text-[10px] uppercase tracking-widest text-[#ff4655] mb-2">
                Email For Confirmation
              </label>
              <input
                id="order-email"
                data-testid="order-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-sm px-3 py-2.5 placeholder:text-zinc-600 focus:outline-none focus:border-[#ff4655]"
              />
            </div>
            <button
              type="submit"
              data-testid="order-submit-btn"
              disabled={loading}
              className="w-full py-3 bg-[#ff4655] hover:bg-white hover:text-black disabled:opacity-60 text-white font-extrabold text-xs uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? "Creating Order..." : "Generate Order Reference"}
            </button>
            <p className="text-zinc-500 text-[10px] font-mono-x leading-relaxed">
              Your reference links your payment receipt to your Discord account for verification. A copy is emailed to you instantly.
            </p>
          </form>
        ) : (
          <div className="mt-2 space-y-5">
            <div className="p-5 bg-zinc-900/70 border border-[#ff4655]/40 text-center">
              <div className="flex items-center justify-center gap-2 text-[#ff4655] mb-2">
                <CheckCircle2 className="w-4 h-4" />
                <span className="font-mono-x text-[10px] uppercase tracking-widest">Awaiting Payment</span>
              </div>
              <div data-testid="order-reference-code" className="font-display text-4xl text-white tracking-widest">
                {order.reference}
              </div>
              <div className="text-zinc-400 text-xs mt-2 font-mono-x">
                {order.plan_name} · €{order.price_eur} · {order.game}
              </div>
            </div>

            <button
              type="button"
              data-testid="order-copy-btn"
              onClick={copyReference}
              className="w-full py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white font-bold text-xs uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
            >
              <Copy className="w-3.5 h-3.5" /> Copy Reference
            </button>

          <a
            href={`${CHECKOUT_LINKS[order.plan]}?client_reference_id=${order.reference}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="order-pay-btn"
            className="block w-full text-center py-3 bg-white hover:bg-[#ff4655] hover:text-white text-black font-extrabold text-xs uppercase tracking-widest transition-colors"
          >
            Pay €{order.price_eur} Now — Secure Checkout
          </a>

          <ol className="text-xs text-zinc-400 space-y-2 list-decimal list-inside">
            <li>Complete payment above. Add your reference ({order.reference}) in the checkout note if a field is available.</li>
            <li>Save your payment receipt.</li>
            <li>Send the receipt + reference in our Discord. We verify and deliver your model files digitally.</li>
          </ol>

          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="order-discord-btn"
            className="block w-full text-center py-3 bg-[#ff4655] hover:bg-white hover:text-black text-white font-extrabold text-xs uppercase tracking-widest transition-colors"
          >
            Open Discord To Verify
          </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OrderDialog;
